// Phase 5 (web revival) — /shared-with-me timeline.
//
// Same virtualized grid as /library, but bound to
// /v1/feed/shared-with-me. Response shape is identical
// (SharedWithMeResponse = AssetListResponse), so we can reuse the
// TimelineGrid component verbatim. No filter bar — the feed is
// authoritative.

import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { api } from '../api/client';
import type { SharedWithMeResponse } from '../api/types';
import { qk } from '../app/queryClient';
import { setLightboxSource } from '../app/lightboxNav';
import { TimelineGrid } from '../components/timeline/TimelineGrid';

export function SharedWithMePage() {
  const queryFn = useCallback(
    ({ pageParam }: { pageParam: string | null }) =>
      api.get<SharedWithMeResponse>('/v1/feed/shared-with-me', {
        cursor: pageParam ?? undefined,
        limit: 100,
        kind: 'all',
      }),
    [],
  );

  const query = useInfiniteQuery({
    queryKey: qk.feedSharedWithMe(),
    initialPageParam: null as string | null,
    queryFn,
    getNextPageParam: (page) => page.next_cursor,
  });

  // Source the lightbox's prev/next + forward pagination from this feed.
  useEffect(() => {
    setLightboxSource({
      queryKey: qk.feedSharedWithMe(),
      initialPageParam: null,
      queryFn,
      getNextPageParam: (page) => page.next_cursor,
    });
  }, [queryFn]);

  const flatAssets = useMemo(
    () => (query.data?.pages ?? []).flatMap((p) => p.assets),
    [query.data],
  );

  return (
    <div className="flex h-full flex-col bg-neutral-950">
      <header className="flex h-12 shrink-0 items-center border-b border-white/5 px-4">
        <h1 className="font-display text-[17px] font-medium tracking-tight text-neutral-100">
          Shared with me
        </h1>
      </header>
      <div className="flex-1 overflow-hidden">
        {query.isError ? (
          <div className="flex h-full items-center justify-center text-sm text-red-400">
            Failed to load: {(query.error as Error).message}
          </div>
        ) : query.isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-neutral-500">
            Loading…
          </div>
        ) : (
          <TimelineGrid
            assets={flatAssets}
            hasNextPage={query.hasNextPage}
            isFetchingNextPage={query.isFetchingNextPage}
            fetchNextPage={() => void query.fetchNextPage()}
            scrollKey="shared-with-me"
            emptyLabel="Nothing shared with you yet."
          />
        )}
      </div>
    </div>
  );
}
